import React, { useState, useEffect, useMemo } from 'react';
import { PRODUCTION_CAPITAL_CAMPAIGN, isProductionCapitalCampaignActive } from '../lib/productionCapitalCampaign';

interface Props {
  className?: string;
  compact?: boolean;
  header?: boolean;
}

/**
 * Standalone Production Capital campaign countdown banner.
 * Counts down to PRODUCTION_CAPITAL_CAMPAIGN.endDate (the 30-day campaign window).
 * Returns null when the campaign is not active or has ended, so it is safe to
 * mount on any page/dashboard — it only renders during the live campaign.
 */
export const ProductionCapitalCountdown: React.FC<Props> = ({ className = '', compact = false, header = false }) => {
  const s = compact || header ? 0.5 : 1;
  const fontS = header ? s * 2.25 : s;
  const active = useMemo(
    () => isProductionCapitalCampaignActive({ paidSharesSold: 0 }),
    []
  );

  const target = useMemo(() => Date.parse(PRODUCTION_CAPITAL_CAMPAIGN.endDate), []);

  const compute = React.useCallback(() => {
    const distance = Math.max(0, target - Date.now());
    return {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000),
      total: distance,
    };
  }, [target]);

  const [t, setT] = useState(compute);

  useEffect(() => {
    setT(compute());
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
  }, [compute]);

  if (!active || t.total <= 0) return null;

  const cell = (value: number, label: string) => (
    <div style={{
      backgroundColor: 'rgba(217, 119, 6, 0.2)',
      border: '1px solid #d97706',
      borderRadius: `${6 * s}px`,
      padding: `${4 * s}px ${8 * s}px`,
      minWidth: header ? 0 : `${44 * s}px`,
      flex: header ? '1 1 0' : undefined,
      maxHeight: header ? '100%' : undefined,
      textAlign: 'center',
    }}>
      <div style={{ color: '#fbbf24', fontSize: `${16 * fontS}px`, fontWeight: 'bold', lineHeight: '1', fontFamily: 'monospace' }}>
        {String(value).padStart(2, '0')}
      </div>
      <div style={{ color: '#d97706', fontSize: `${10 * fontS}px`, fontWeight: '600', marginTop: `${2 * s}px`, letterSpacing: '0.5px' }}>
        {label}
      </div>
    </div>
  );

  return (
    <div className={`${className} ${header ? 'user-dashboard-header-pcap' : ''}`.trim()} style={{
      background: 'linear-gradient(135deg, rgba(41, 37, 36, 0.95) 0%, rgba(28, 25, 23, 0.95) 100%)',
      border: '1px solid rgba(217, 119, 6, 0.5)',
      borderRadius: `${8 * s}px`,
      padding: header ? '6px 10px' : `${10 * s}px ${16 * s}px`,
      boxShadow: header ? 'none' : `0 ${4 * s}px ${16 * s}px rgba(0, 0, 0, 0.4)`,
      backdropFilter: 'blur(10px)',
      maxWidth: header ? '100%' : (compact ? undefined : `${1200 * s}px`),
      width: header ? '100%' : (compact ? 'fit-content' : undefined),
      height: header ? '100%' : undefined,
      maxHeight: header ? '100%' : undefined,
      minWidth: header ? 0 : undefined,
      minHeight: header ? 0 : undefined,
      boxSizing: header ? 'border-box' : undefined,
      display: header ? 'flex' : undefined,
      flexDirection: header ? 'column' : undefined,
      justifyContent: header ? 'center' : undefined,
      overflow: header ? 'hidden' : undefined,
      margin: header ? 0 : `0 auto ${8 * s}px`,
      position: header ? 'relative' : undefined,
    }}>
      <div style={{ textAlign: 'center', marginBottom: `${6 * s}px`, minWidth: 0, width: header ? '100%' : undefined }}>
        <div style={{
          color: '#fbbf24',
          fontSize: `${15 * fontS}px`,
          fontWeight: '700',
          letterSpacing: `${1 * s}px`,
          marginBottom: `${2 * s}px`,
          textTransform: 'uppercase',
          overflow: header ? 'hidden' : undefined,
          textOverflow: header ? 'ellipsis' : undefined,
          whiteSpace: header ? 'nowrap' : undefined,
        }}>
          {PRODUCTION_CAPITAL_CAMPAIGN.name}
        </div>
        <div style={{
          color: '#d97706',
          fontSize: `${11 * fontS}px`,
          fontWeight: '600',
          overflow: header ? 'hidden' : undefined,
          textOverflow: header ? 'ellipsis' : undefined,
          whiteSpace: header ? 'nowrap' : undefined,
        }}>
          ${PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd} per paid share — Packages 1-5 only
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: `${8 * s}px`, minWidth: 0, width: header ? '100%' : undefined }}>
        {cell(t.days, 'DAYS')}
        {cell(t.hours, 'HRS')}
        {cell(t.minutes, 'MINS')}
        {cell(t.seconds, 'SECS')}
      </div>
    </div>
  );
};

export default ProductionCapitalCountdown;
